import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppTrSettings, AppTranslate } from "./AppTranslate";
import { AppTrChat, AppTranslateRules } from "./AppTranslateRules";
import { AppTranslateView } from "./AppTranslateView";
import { AppTranslator, IAppTrHeard, IAppTrSaved } from "./AppTranslator";
import { AppFocusKeep, IFocusDoc, IFocusEl } from "./AppFocus";

// AppTranslateChip - ONE CHAT'S TRANSLATION CONTROLS ON ONE SCREEN (owner, 2026-09-27; moved 2026-10-13: "Move the
// translation settings into a new chat settings dialog"). The same controller serves two screens:
//   - the CHAT HEADER (`Editor` false): the state chip "EN ⇄ ID" (only while the chat is translated) and the one quiet
//     line under the header (the service's suggestion with its one tap, the monthly cap, an unreadable file);
//   - the "CHAT SETTINGS" DIALOG'S TRANSLATION SECTION (`Editor` true): the settings themselves (on/off, my language,
//     their language - Save = `/translate set`, which carries a stored glossary along unchanged) with the same quiet line above them.
// ⚠ Until 2026-10-13 the settings were a panel the chip toggled open under the header; "open" and "cancel" are gone
// with it - the editor is simply there while its dialog is.
//
// ⛔ NOTHING IS DRAWN UNLESS THE SERVICE SAYS TRANSLATION IS AVAILABLE. A dormant service (no route -> `Off`) or no AI
//    route for this subscriber (`available: false`) = no chip, no line, no editor, nothing asked again this visit.
// ⛔ THE SUGGESTION NEVER SWITCHES ANYTHING ON: only the owner's tap on its "Translate" saves the settings.
// ⛔ AN UNREADABLE SETTINGS FILE IS NEVER SAVED OVER from here: the sentence is shown and there is no Save.
// ⛔ NOTHING HERE SENDS A MESSAGE.
// ⚠ SHAPED BY AN INTERFACE (`IAppTrScreen`) so the suite drives it without a browser; `AppTrDomScreen` is the page.

/** Where the controller draws, and how it reads the editor's fields. */
export interface IAppTrScreen {
    /** "" hides the chip. */
    Chip(Html: string): void;
    /** "" hides the line. */
    Line(Html: string, Bad: boolean): void;
    /** The settings editor; "" hides it. */
    Panel(Html: string): void;
    Value(Id: string): string;
    Checked(Id: string): boolean;
}

/** Told after a save landed - the page updates the send path, the other tabs and (switched on) the backfill. */
export interface IAppTrChipHost {
    Changed(Href: string, TurnedOn: boolean): void;
    /** The controller learned (or re-learned) this chat's state. */
    Heard(): void;
}

export class AppTranslateChip implements IAppTrHeard, IAppTrSaved {
    public static readonly SavedWords: string = "Saved.";
    private Translator: AppTranslator;
    private Screen: IAppTrScreen;
    private Host: IAppTrChipHost;
    private Href: string;
    private Title: string;
    /** This screen has the settings editor (the dialog); false = the chat header (chip + line only). */
    private Editor: boolean;
    /** The editor's draft was taken from what the service last said (a re-read by another tab takes it again). */
    private Drafted: boolean = false;
    private Draft: AppTrSettings = new AppTrSettings();
    private Busy: string = "";
    private Note: string = "";
    private Bad: boolean = false;
    private Dismissed: boolean = false;
    private WasActive: boolean = false;
    private Stopped: boolean = false;
    /** A re-ask after a `retry` refusal is scheduled (one at a time). */
    private RetryArmed: boolean = false;

    public constructor(Translator: AppTranslator, Screen: IAppTrScreen, Host: IAppTrChipHost, Href: string, Title: string, Editor: boolean = false) {
        this.Translator = Translator;
        this.Screen = Screen;
        this.Host = Host;
        this.Href = nz(Href);
        this.Title = nz(Title);
        this.Editor = Editor === true;
    }

    /** Ask the service (once for this chat in this visit) and draw what it says. */
    public Start = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Translator.Ask(this.Href, this);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateChip.Start");
        }
    };

    /** Another tab changed this chat's settings: read them again. ⛔ Reads only. */
    public Reload = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true) { return; }
            this.Drafted = false;
            this.Translator.Book.Forget(this.Href);
            this.Translator.Ask(this.Href, this);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateChip.Reload");
        }
    };

    /** The `retry` wait is over: ask again unless the view was replaced. */
    private Retry = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.RetryArmed = false;
            if (this.Stopped === true) { return; }
            this.Translator.Ask(this.Href, this);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateChip.Retry");
        }
    };

    /** The view was replaced / the dialog closed. DOCUMENTED EXCEPTION: a field write. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public Stop = (): void => {
        this.Stopped = true;
    };

    /** The page this controller belongs to. DOCUMENTED EXCEPTION: a field read. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public Page = (): string => {
        return this.Href;
    };

    /** `IAppTrHeard`: this chat's state arrived. */
    public Heard = (Href: string, Chat: AppTrChat): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true || Href !== this.Href) { return; }
            this.WasActive = AppTranslate.Active(Chat.Settings);
            this.Paint();
            this.Host.Heard();
            // ⭐ A REFUSAL THAT CLEARS ON ITS OWN (the service just restarted): ask again later, once at a time, only
            // while this view is still the one on screen (`Retry` checks `Stopped`).
            if (Chat.Known !== true && Chat.RetrySec > 0 && this.RetryArmed === false) {
                this.RetryArmed = true;
                this.Translator.Later.After(Chat.RetrySec * 1000, this.Retry);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateChip.Heard");
        }
    };

    /** Is translation offered for this chat at all? Known, available, and the route exists. */
    public Shown = (): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Chat: AppTrChat = this.Translator.Book.Of(this.Href);
            return this.Translator.Book.Off === false && Chat.Known === true && Chat.Available === true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateChip.Shown");
            return false;
        }
    };

    /** Draw the chip, the line and (on the dialog's screen) the editor from what the book knows. */
    public Paint = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true) { return; }
            if (this.Shown() === false) { this.Screen.Chip(""); this.Screen.Line("", false); this.Screen.Panel(""); return; }
            const Chat: AppTrChat = this.Translator.Book.Of(this.Href);
            this.Screen.Chip(AppTranslateView.Chip(Chat.Settings));
            this.PaintLine(Chat);
            // ⛔ An unreadable file: the line says it (above), and there is no editor - so no Save over it.
            if (this.Editor === false || Chat.Unreadable === true) { this.Screen.Panel(""); return; }
            if (this.Drafted === false) { this.Draft = AppTranslateRules.DraftOf(Chat); this.Drafted = true; }
            this.Screen.Panel(AppTranslateView.Panel(this.Draft, this.Busy, this.Note, this.Bad));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateChip.Paint");
        }
    };

    /** The one quiet line: unreadable beats the cap, the cap beats the suggestion. */
    private PaintLine = (Chat: AppTrChat): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Chat.Unreadable === true) { this.Screen.Line(AppTranslateView.Text(AppTranslateView.UnreadableWhy), true); return; }
            if (Chat.Capped === true) { this.Screen.Line(AppTranslateView.Text(AppTranslate.CappedWhy), false); return; }
            const Suggest: string = AppTranslateRules.SuggestLine(this.Title, Chat);
            if (Suggest.length > 0 && this.Dismissed === false) {
                this.Screen.Line(AppTranslateView.Suggest(Suggest), false);
                return;
            }
            this.Screen.Line("", false);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateChip.PaintLine");
        }
    };

    /** A press on one of the controls (`data-wac-tract`). */
    public Act = (Action: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true || this.Shown() === false) { return; }
            if (Action === "accept") { this.Accept(); return; }
            if (Action === "dismiss") { this.Dismissed = true; this.Paint(); return; }
            if (this.Editor === false) { return; }
            if (Action === "save") { this.Save(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateChip.Act");
        }
    };

    /** ⛔ THE SUGGESTION'S ONE TAP - the owner's act, and the only way it switches translation on. */
    private Accept = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Settings: AppTrSettings | null = AppTranslateRules.Accepted(this.Translator.Book.Of(this.Href));
            if (Settings === null || this.Busy.length > 0) { return; }
            this.Busy = "Switching on…";
            this.Screen.Line(AppTranslateView.Text("Switching translation on…"), false);
            this.Translator.Save(this.Href, Settings as AppTrSettings, this);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateChip.Accept");
        }
    };

    /** Read the editor's languages and switch into the draft (before any repaint loses them). */
    private Take = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Draft.MyLang = this.Screen.Value(AppTranslateView.MyId);
            this.Draft.TheirLang = this.Screen.Value(AppTranslateView.TheirId);
            this.Draft.On = this.Screen.Checked(AppTranslateView.OnId);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateChip.Take");
        }
    };

    private Save = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Busy.length > 0 || this.Translator.Book.Of(this.Href).Unreadable === true) { return; }
            this.Take();
            const Why: string = AppTranslateRules.SaveWhy(this.Draft);
            if (Why.length > 0) { this.Note = Why; this.Bad = true; this.Paint(); return; }
            this.Busy = "Saving…";
            this.Note = "";
            this.Paint();
            this.Translator.Save(this.Href, this.Draft, this);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateChip.Save");
        }
    };

    /** `IAppTrSaved`: the service answered the save. */
    public Saved = (Href: string, Why: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true || Href !== this.Href) { return; }
            this.Busy = "";
            if (Why.length > 0) {
                this.Note = Why; this.Bad = true; this.Paint();
                // The header has no editor to carry the note: its line says it.
                if (this.Editor === false) { this.Screen.Line(AppTranslateView.Text(Why), true); }
                return;
            }
            const Now: boolean = AppTranslate.Active(this.Translator.Book.Of(this.Href).Settings);
            const TurnedOn: boolean = Now === true && this.WasActive === false;
            this.WasActive = Now;
            // ⭐ The editor stays (the dialog is still open) and shows what the service now holds.
            this.Drafted = false;
            this.Note = "";
            if (this.Editor === true) { this.Note = AppTranslateChip.SavedWords; }
            this.Bad = false;
            this.Paint();
            this.Host.Changed(this.Href, TurnedOn);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTranslateChip.Saved");
        }
    };
}

/**
 * The page's own elements. ⛔ Hiding is the `hidden` attribute - never an inline style. One class for both screens:
 * the chat header (`new AppTrDomScreen()`: the chip's slot and the line; no editor) and the dialog's Translation section
 * (`new AppTrDomScreen("", LineId, PanelId, BoxId)`: no chip; `BoxId` = the section, shown while it has anything in it).
 */
export class AppTrDomScreen implements IAppTrScreen {
    private ChipId: string;
    private LineId: string;
    private PanelId: string;
    private BoxId: string;

    public constructor(ChipId: string = AppTranslateView.ChipId, LineId: string = AppTranslateView.LineId, PanelId: string = "", BoxId: string = "") {
        this.ChipId = ChipId;
        this.LineId = LineId;
        this.PanelId = PanelId;
        this.BoxId = BoxId;
    }

    public Chip = (Html: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            AppTrDomScreen.Fill(this.ChipId, Html);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrDomScreen.Chip");
        }
    };

    public Line = (Html: string, Bad: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const El: HTMLElement | null = AppTrDomScreen.Fill(this.LineId, Html);
            if (El === null) { return; }
            let Cls: string = "trline";
            if (Bad === true) { Cls = "trline bad"; }
            if (El.className !== Cls) { El.className = Cls; }
            this.ShowBox();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrDomScreen.Line");
        }
    };

    public Panel = (Html: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            AppTrDomScreen.Fill(this.PanelId, Html);
            this.ShowBox();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrDomScreen.Panel");
        }
    };

    /** The section that holds the line and the editor is there only while one of them has something in it. */
    private ShowBox = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.BoxId.length === 0) { return; }
            const Box: HTMLElement | null = document.getElementById(this.BoxId);
            if (Box === null) { return; }
            const Line: HTMLElement | null = document.getElementById(this.LineId);
            const Panel: HTMLElement | null = document.getElementById(this.PanelId);
            const Empty: boolean = (Line === null || Line.hidden === true) && (Panel === null || Panel.hidden === true);
            if (Box.hidden !== Empty) { Box.hidden = Empty; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrDomScreen.ShowBox");
        }
    };

    /**
     * Put escaped markup (built by `AppTranslateView`) into one element; "" empties and hides it. An element this
     * screen does not have ("" id, or not on the page) is simply not drawn.
     * ⭐ The keyboard's place inside it survives (`AppFocusKeep`): the editor is redrawn on every press, and the button
     * that was just pressed used to go with the old markup.
     * ⛔ WRITTEN ONLY WHEN IT CHANGED: the line is a live region (a rewrite is read out again), and a control must not be
     * rebuilt under the pointer for nothing.
     */
    private static Fill = (Id: string, Html: string): HTMLElement | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Id.length === 0) { return null; }
            const El: HTMLElement | null = document.getElementById(Id);
            if (El === null) { return null; }
            if (El.innerHTML !== Html && AppTrDomScreen.Last.get(El) !== Html) {
                AppFocusKeep.Swap(document as unknown as IFocusDoc, El as unknown as IFocusEl & { innerHTML: string }, Html);
            }
            AppTrDomScreen.Last.set(El, Html);
            if (Html.length === 0) { El.setAttribute("hidden", ""); } else { El.removeAttribute("hidden"); }
            return El;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrDomScreen.Fill");
            return null;
        }
    };

    /** What was last written into each element (the browser re-serialises markup, so `innerHTML` alone cannot be compared). */
    private static Last: WeakMap<HTMLElement, string> = new WeakMap<HTMLElement, string>();

    public Value = (Id: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const El: HTMLInputElement | null = document.getElementById(Id) as HTMLInputElement | null;
            if (El === null) { return ""; }
            return nz(El.value);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrDomScreen.Value");
            return "";
        }
    };

    public Checked = (Id: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const El: HTMLInputElement | null = document.getElementById(Id) as HTMLInputElement | null;
            return El !== null && El.checked === true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTrDomScreen.Checked");
            return false;
        }
    };
}
